import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { opsErrorMessage, searchShops } from '../lib/ops-api';
import { shopFilePath } from '../lib/shop-file';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { Badge } from '../components/Pill';
import { useLocale } from '../i18n/LocaleProvider';

/** Catalogue key per matched column — so an agent can tell a name match from a login match. */
const MATCH_LABEL_KEY: Record<string, string> = {
  domain: 'shopSearch.match.domain',
  name: 'shopSearch.match.name',
  supportEmail: 'shopSearch.match.supportEmail',
  smtpFromAddress: 'shopSearch.match.smtpFromAddress',
  sessionEmail: 'shopSearch.match.sessionEmail',
};

/**
 * The entry to the shop file: find a merchant by domain, name or an email they write from.
 *
 * The search shows identity only. Opening a hit asks for a reason first — see `ShopFileLayout`.
 * The term lives in the URL, so a search can be linked to a colleague and survives a reload.
 */
export function ShopSearchPage() {
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const submitted = searchParams.get('q') ?? '';
  const [term, setTerm] = useState(submitted);

  const results = useQuery({
    queryKey: ['shop-search', submitted],
    queryFn: () => searchShops(submitted),
    enabled: submitted.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

  function submit(): void {
    const next = term.trim();
    setSearchParams(
      next ? new URLSearchParams({ q: next }) : new URLSearchParams(),
    );
  }

  const hits = results.data?.data ?? [];

  return (
    <>
      <PageHead
        eyebrow={t('shopSearch.eyebrow')}
        title={t('shopSearch.title')}
        lead={t('shopSearch.lead')}
      />
      <PageBody>
        <div className="surface p-4 flex flex-wrap gap-3 items-center">
          <input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
            placeholder={t('shopSearch.placeholder')}
            aria-label={t('shopSearch.searchLabel')}
            className="field-input flex-1 min-w-[min(240px,100%)]"
          />
          <button
            type="button"
            onClick={submit}
            className="btn-primary btn-sm"
            disabled={term.trim().length < 2}
          >
            {t('shopSearch.search')}
          </button>
        </div>

        {results.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(results.error, t('shopSearch.failed'))}
          </StatusBanner>
        )}

        {results.data && hits.length === 0 && (
          <StatusBanner tone="info">{t('shopSearch.none')}</StatusBanner>
        )}

        {results.data?.truncated && (
          <StatusBanner tone="warning">
            {t('shopSearch.truncated')}
          </StatusBanner>
        )}

        {hits.length > 0 && (
          <div className="surface overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('shopSearch.columnShop')}</th>
                  <th>{t('shopSearch.columnName')}</th>
                  <th>{t('shopSearch.columnMatch')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hits.map((hit) => (
                  <tr key={hit.shopId}>
                    <td className="font-mono text-sm">{hit.shopDomain}</td>
                    <td>{hit.name ?? '—'}</td>
                    <td>
                      <Badge tone="gray">
                        {t(
                          MATCH_LABEL_KEY[hit.matchedOn] ??
                            'shopSearch.match.domain',
                        )}
                      </Badge>
                      {hit.pendingDeletion && (
                        <span className="ml-2">
                          <Badge tone="red">
                            {t('shopFile.pendingDeletion')}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <Link
                        to={shopFilePath(hit.shopId)}
                        className="link-arrow"
                      >
                        {t('shopSearch.open')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
    </>
  );
}
